/**
 * What a room type costs per night at one event. A row holds one price for every night (`base_cents`) and, where a
 * night differs, that night's own price (`nights[date]`). The price of a night = its own price, else the base.
 * Keys outside the event's current dates stay in the row and are ignored here.
 */
/** `occupancy`: cents more per night from a number of people in the room (see ./occupancy). Absent on rows read before it existed. */
export interface PriceRow { hotel_id: string; code: string; base_cents: number | null; nights: Record<string, number>; occupancy?: Record<string, number> }
export type PriceSummary = { kind: 'none' } | { kind: 'flat'; cents: number } | { kind: 'varies'; min: number; max: number; own: number };

export const MAX_PRICE_CENTS = 10_000_000;

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function localDay(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v ?? '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(d.getTime()) ? d : null;
}

/** The nights of an event: its first day up to the day before departure; a one-day event has one night. */
export function eventNights(start: string, end: string): string[] {
  const first = localDay(start);
  if (!first) return [];
  const depart = localDay(end) ?? first;
  const out = [iso(first)];
  for (let i = 1; i < 366; i += 1) {
    const d = new Date(first.getFullYear(), first.getMonth(), first.getDate() + i);
    if (d.getTime() >= depart.getTime()) break;
    out.push(iso(d));
  }
  return out;
}

export function effective(row: PriceRow | null | undefined, night: string): number | null {
  return row?.nights?.[night] ?? row?.base_cents ?? null;
}

export function summary(row: PriceRow | null | undefined, nights: readonly string[]): PriceSummary {
  if (!row) return { kind: 'none' };
  const own = nights.filter((n) => typeof row.nights?.[n] === 'number').length;
  if (own > 0) {
    const prices = nights.map((n) => effective(row, n)).filter((p): p is number => p !== null);
    return { kind: 'varies', min: Math.min(...prices), max: Math.max(...prices), own };
  }
  return row.base_cents == null ? { kind: 'none' } : { kind: 'flat', cents: row.base_cents };
}

/** A group's types do not all cost the same: the one price for every night and each night are compared; priced against unpriced differs. */
export function pricesDiffer(rows: ReadonlyArray<PriceRow | null | undefined>, nights: readonly string[]): boolean {
  const seen = new Set(rows.map((r) => JSON.stringify([r?.base_cents ?? null, ...nights.map((n) => effective(r, n))])));
  return seen.size > 1;
}

/** `180`, `180.5`, `$1,234.50` → cents; blank → null (no price). Refuses negatives, a third decimal, and above 100,000.00. */
export function parsePrice(text: string): { ok: true; cents: number | null } | { ok: false } {
  const t = text.trim();
  if (t === '') return { ok: true, cents: null };
  const m = /^\$?\s*(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return { ok: false };
  const cents = Number(m[1].replace(/,/g, '')) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  if (!Number.isSafeInteger(cents) || cents > MAX_PRICE_CENTS) return { ok: false };
  return { ok: true, cents };
}

/** The plain amount a price field holds while it is edited: `180.00`, no symbol. */
export function plainAmount(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '';
  const whole = Math.trunc(cents / 100);
  return `${whole}.${pad(Math.abs(cents) % 100)}`;
}

/** The one sentence shown when a typed price can't be read. */
export const PRICE_NOT_READ = 'Enter a price like 180 or 180.50.';
